import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAnalytics } from '@/hooks/supabase/useAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Download, TrendingUp, MapPin } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import UsageMap from './UsageMap';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import 'mapbox-gl/dist/mapbox-gl.css';

const AnalyticsDashboard = () => {
  const { data, loading, error, refresh } = useAnalytics();
  const [isBackfilling, setIsBackfilling] = React.useState(false);
  const [backfillMessage, setBackfillMessage] = React.useState('');

  const handleBackfill = async () => {
    setIsBackfilling(true);
    setBackfillMessage('Initiating backfill process...');
    const jobId = uuidv4();
    const channel = supabase.channel(`backfill-progress-${jobId}`);

    const cleanup = () => {
      console.log('Cleaning up channel subscription.');
      supabase.removeChannel(channel);
      setIsBackfilling(false);
      // Clear the message after a delay
      setTimeout(() => {
        setBackfillMessage('');
      }, 30000);
    };

    // Set a timeout to clean up in case the 'done' message never arrives
    const jobTimeout = setTimeout(() => {
      setBackfillMessage(prev => `${prev}\n\nWarning: Job timed out. If it's still running, progress may not be shown.`);
      cleanup();
    }, 15 * 60 * 1000); // Increased to 15 minutes to allow for longer processing

    channel
      .on('broadcast', { event: 'progress' }, ({ payload }) => {
        console.log('Progress update:', payload.message);
        setBackfillMessage(currentMessages => `${currentMessages}\n${payload.message}`);
      })
      .on('broadcast', { event: 'done' }, ({ payload }) => {
        clearTimeout(jobTimeout); // Clear the safety timeout
        if (payload.error) {
          setBackfillMessage(prev => `${prev}\n\n--- ERROR ---\n${payload.error}`);
        } else {
          setBackfillMessage(prev => `${prev}\n\n--- FINAL SUMMARY ---\n${payload.message}`);
          refresh(); // Refresh analytics data on success
        }
        cleanup();
      })
      .subscribe(async (status) => {
        if (status !== 'SUBSCRIBED') {
          clearTimeout(jobTimeout);
          setBackfillMessage('Error: Could not connect to real-time progress updates.');
          cleanup();
          return;
        }

        console.log('Channel subscribed, invoking function...');
        try {
          const { error: invokeError } = await supabase.functions.invoke('backfill-analytics-locations', {
            body: { jobId },
          });
          
          if (invokeError) {
            throw invokeError;
          }
          // The function now returns immediately, so we just wait for realtime messages.
          setBackfillMessage(prev => `${prev}\nServer accepted the job. Waiting for progress...`);

        } catch (error: any) {
          clearTimeout(jobTimeout);
          console.error('Backfill invocation error:', error);
          setBackfillMessage(prev => `${prev}\n\nError invoking function: ${error.message}`);
          cleanup();
        }
      });
  };

  const handleExport = () => {
    if (!data) return;

    const wb = XLSX.utils.book_new();

    const kpis = [
      { KPI: 'Daily Active Users (DAU)', Value: data.user_activity?.dau ?? 0 },
      { KPI: 'Weekly Active Users (WAU)', Value: data.user_activity?.wau ?? 0 },
      { KPI: 'Monthly Active Users (MAU)', Value: data.user_activity?.mau ?? 0 },
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(kpis), 'Key Metrics');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.new_signups || []), 'New Signups (30d)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.token_usage || []), 'Token Usage (30d)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.cohort_analysis || []), 'Cohort Analysis');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.model_token_usage || []), 'Model Token Usage (30d)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.model_token_usage_60d || []), 'Model Token Usage (60d)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.model_token_usage_90d || []), 'Model Token Usage (90d)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.model_usage_by_territory || []), 'Territory Usage (30d)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.model_usage_by_territory_60d || []), 'Territory Usage (60d)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.model_usage_by_territory_90d || []), 'Territory Usage (90d)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.token_usage_by_location || []), 'Token Usage By Location (30d)');

    XLSX.writeFile(wb, 'CanXP_Analytics_Export.xlsx');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
            <p className="text-muted-foreground">Track key performance indicators and model usage trends</p>
          </div>
          <div className="flex gap-2">
            <Button disabled variant="outline">
              Backfilling...
            </Button>
            <Button disabled>
              <Download className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-80 col-span-full" />
          <Skeleton className="h-80 col-span-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="2xl font-bold">Analytics Dashboard</h2>
            <p className="text-muted-foreground">Track key performance indicators and model usage trends</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleBackfill} disabled={isBackfilling} variant="outline">
              {isBackfilling ? 'Backfilling...' : 'Backfill Locations'}
            </Button>
            <Button onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
          </div>
        </div>
        <p className="text-destructive">Error loading analytics: {error.message}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
            <p className="text-muted-foreground">Track key performance indicators and model usage trends</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleBackfill} disabled={isBackfilling} variant="outline">
              {isBackfilling ? 'Backfilling...' : 'Backfill Locations'}
            </Button>
            <Button onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
          </div>
        </div>
        <p>No analytics data available.</p>
      </div>
    );
  }

  const formatDateData = (items: any[]) => items?.map(item => ({ ...item, date: format(new Date(item.date), 'MMM d') })) || [];
  const formatCohortData = (items: any[]) => items?.map(item => ({ ...item, cohort_month: format(new Date(item.cohort_month), 'MMM yyyy'), avg_tokens_first_30_days: Math.round(item.avg_tokens_first_30_days) })) || [];

  // New helper functions for multi-line charts
  const transformGroupedDataForLineChart = (sourceData: any[], groupField: string, valueField: string) => {
    if (!sourceData || sourceData.length === 0) return [];

    // Filter out "Unknown" provinces
    const filteredData = sourceData.filter(item => item[groupField] !== 'Unknown');
    
    // Determine the full date range
    const allDates = Array.from(new Set(filteredData.map(item => item.date))).sort();
    const uniqueGroupNames = Array.from(new Set(filteredData.map(item => item[groupField])));

    const groupedByDate = new Map<string, { date: string; _originalDate: Date; [key: string]: any }>();

    // Initialize all dates with 0 for all groups
    allDates.forEach(dateString => {
      const originalDate = new Date(dateString);
      const formattedDate = format(originalDate, 'MMM d');
      const initialEntry: { date: string; _originalDate: Date; [key: string]: any } = { date: formattedDate, _originalDate: originalDate };
      uniqueGroupNames.forEach(groupName => {
        initialEntry[groupName as string] = 0;
      });
      groupedByDate.set(formattedDate, initialEntry);
    });

    // Populate with actual data
    filteredData.forEach(item => {
        const originalDate = new Date(item.date);
        const formattedDate = format(originalDate, 'MMM d');
        const groupKey = item[groupField];

        const dateEntry = groupedByDate.get(formattedDate);
        if (dateEntry) {
          dateEntry[groupKey] = (dateEntry[groupKey] || 0) + item[valueField];
        }
    });

    // Convert map values to an array and sort by original date to ensure correct chronological order
    const sortedData = Array.from(groupedByDate.values()).sort((a, b) => {
        return a._originalDate.getTime() - b._originalDate.getTime();
    }).map(({ _originalDate, ...rest }) => rest); // Remove _originalDate before returning

    return sortedData;
  };

  const getUniqueGroupNames = (sourceData: any[], groupField: string) => {
    if (!sourceData) return [];
    const groupNames = new Set<string>();
    sourceData.forEach(item => {
      if (item[groupField] !== 'Unknown') {
        groupNames.add(item[groupField]);
      }
    });
    return Array.from(groupNames);
  };

  // Apply new transformation for model usage
  const transformedModelUsage30d = transformGroupedDataForLineChart(data.model_token_usage, 'model_name', 'total_tokens');
  const uniqueModelNames30d = getUniqueGroupNames(data.model_token_usage, 'model_name');

  const transformedModelUsage60d = transformGroupedDataForLineChart(data.model_token_usage_60d, 'model_name', 'total_tokens');
  const uniqueModelNames60d = getUniqueGroupNames(data.model_token_usage_60d, 'model_name');

  const transformedModelUsage90d = transformGroupedDataForLineChart(data.model_token_usage_90d, 'model_name', 'total_tokens');
  const uniqueModelNames90d = getUniqueGroupNames(data.model_token_usage_90d, 'model_name');

  // Apply new transformation for territory usage
  const transformedTerritoryUsage30d = transformGroupedDataForLineChart(data.model_usage_by_territory, 'territory', 'total_tokens');
  const uniqueTerritoryNames30d = getUniqueGroupNames(data.model_usage_by_territory, 'territory');

  const transformedTerritoryUsage60d = transformGroupedDataForLineChart(data.model_usage_by_territory_60d, 'territory', 'total_tokens');
  const uniqueTerritoryNames60d = getUniqueGroupNames(data.model_usage_by_territory_60d, 'territory');

  const transformedTerritoryUsage90d = transformGroupedDataForLineChart(data.model_usage_by_territory_90d, 'territory', 'total_tokens');
  const uniqueTerritoryNames90d = getUniqueGroupNames(data.model_usage_by_territory_90d, 'territory');
  
  const transformedProvinceUsage30d = transformGroupedDataForLineChart(data.model_usage_by_province, 'province', 'total_tokens');
  const uniqueProvinceNames30d = getUniqueGroupNames(data.model_usage_by_province, 'province');

  const transformedProvinceUsage60d = transformGroupedDataForLineChart(data.model_usage_by_province_60d, 'province', 'total_tokens');
  const uniqueProvinceNames60d = getUniqueGroupNames(data.model_usage_by_province_60d, 'province');

  const transformedProvinceUsage90d = transformGroupedDataForLineChart(data.model_usage_by_province_90d, 'province', 'total_tokens');
  const uniqueProvinceNames90d = getUniqueGroupNames(data.model_usage_by_province_90d, 'province');

  const territoryColors: { [key: string]: string } = {
    'USA': '#8884d8',
    'Canada': '#82ca9d',
    'International': '#ffc658',
    'Unknown': '#b0b0b0',
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
          <p className="text-muted-foreground">Track key performance indicators and model usage trends</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleBackfill} disabled={isBackfilling} variant="outline">
            {isBackfilling ? 'Backfilling...' : 'Backfill Locations'}
          </Button>
          <Button onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Export to Excel
          </Button>
        </div>
      </div>

      {backfillMessage && (
        <Card className="p-4 bg-muted">
          <CardHeader className="p-0 pb-2">
            <CardTitle className="text-base">Backfill Progress</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <pre className="text-sm whitespace-pre-wrap font-mono bg-background p-2 rounded-md max-h-60 overflow-y-auto">
              {backfillMessage}
            </pre>
          </CardContent>
        </Card>
      )}

      <UsageMap />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card><CardHeader><CardTitle>Daily Active Users (DAU)</CardTitle></CardHeader><CardContent><p className="text-4xl font-bold">{data.user_activity?.dau ?? 0}</p></CardContent></Card>
        <Card><CardHeader><CardTitle>Weekly Active Users (WAU)</CardTitle></CardHeader><CardContent><p className="text-4xl font-bold">{data.user_activity?.wau ?? 0}</p></CardContent></Card>
        <Card><CardHeader><CardTitle>Monthly Active Users (MAU)</CardTitle></CardHeader><CardContent><p className="text-4xl font-bold">{data.user_activity?.mau ?? 0}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>New Signups (Last 30 Days)</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={formatDateData(data.new_signups)}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" />
            <YAxis 
              domain={[0, 'auto']} 
              tickCount={6} 
              allowDecimals={false}
            />
            <Tooltip /><Legend /><Line type="monotone" dataKey="count" stroke="#8884d8" name="New Signups" /></LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Cohort Analysis: Average Tokens Used in First 30 Days</CardTitle><CardDescription>This chart shows if new users are more engaged than users from previous months.</CardDescription></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={formatCohortData(data.cohort_analysis)}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="cohort_month" /><YAxis /><Tooltip /><Legend /><Bar dataKey="avg_tokens_first_30_days" fill="#82ca9d" name="Avg Tokens Used" /></BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader><CardTitle>Total Tokens Used (Last 30 Days)</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={formatDateData(data.token_usage)}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" /><YAxis /><Tooltip /><Legend /><Line type="monotone" dataKey="total_tokens" stroke="#ffc658" name="Tokens Used" /></LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Tabs defaultValue="30d" className="w-full">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" />Model Token Usage by Time Period</CardTitle><CardDescription>Track cumulative token usage by model over different time periods</CardDescription></CardHeader>
          <CardContent>
            <TabsList className="grid w-full grid-cols-3 mb-4"><TabsTrigger value="30d">Last 30 Days</TabsTrigger><TabsTrigger value="60d">Last 60 Days</TabsTrigger><TabsTrigger value="90d">Last 90 Days</TabsTrigger></TabsList>
            <TabsContent value="30d">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={transformedModelUsage30d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {uniqueModelNames30d.map((modelName, index) => (
                    <Line key={modelName} type="monotone" dataKey={modelName} stroke={`hsl(${index * 60}, 70%, 50%)`} name={modelName} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
            <TabsContent value="60d">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={transformedModelUsage60d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {uniqueModelNames60d.map((modelName, index) => (
                    <Line key={modelName} type="monotone" dataKey={modelName} stroke={`hsl(${index * 60}, 70%, 50%)`} name={modelName} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
            <TabsContent value="90d">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={transformedModelUsage90d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {uniqueModelNames90d.map((modelName, index) => (
                    <Line key={modelName} type="monotone" dataKey={modelName} stroke={`hsl(${index * 60}, 70%, 50%)`} name={modelName} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      <Tabs defaultValue="30d" className="w-full">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" />Model Usage by Territory</CardTitle><CardDescription>Track token usage by user's geographic territory over different time periods</CardDescription></CardHeader>
          <CardContent>
            <TabsList className="grid w-full grid-cols-3 mb-4"><TabsTrigger value="30d">Last 30 Days</TabsTrigger><TabsTrigger value="60d">Last 60 Days</TabsTrigger><TabsTrigger value="90d">Last 90 Days</TabsTrigger></TabsList>
            <TabsContent value="30d">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={transformedTerritoryUsage30d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {uniqueTerritoryNames30d.map((territoryName, index) => (
                    <Line key={territoryName} type="monotone" dataKey={territoryName} stroke={territoryColors[territoryName] || '#000'} name={territoryName} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
            <TabsContent value="60d">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={transformedTerritoryUsage60d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {uniqueTerritoryNames60d.map((territoryName, index) => (
                    <Line key={territoryName} type="monotone" dataKey={territoryName} stroke={territoryColors[territoryName] || '#000'} name={territoryName} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
            <TabsContent value="90d">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={transformedTerritoryUsage90d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {uniqueTerritoryNames90d.map((territoryName, index) => (
                    <Line key={territoryName} type="monotone" dataKey={territoryName} stroke={territoryColors[territoryName] || '#000'} name={territoryName} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      <Tabs defaultValue="30d" className="w-full">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" />Model Usage by Location (Province/State)</CardTitle><CardDescription>Track token usage by user's province or state over different time periods</CardDescription></CardHeader>
          <CardContent>
            <TabsList className="grid w-full grid-cols-3 mb-4"><TabsTrigger value="30d">Last 30 Days</TabsTrigger><TabsTrigger value="60d">Last 60 Days</TabsTrigger><TabsTrigger value="90d">Last 90 Days</TabsTrigger></TabsList>
            <TabsContent value="30d">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={transformedProvinceUsage30d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {uniqueProvinceNames30d.map((provinceName, index) => (
                    <Line key={provinceName} type="monotone" dataKey={provinceName} stroke={`hsl(${index * 40}, 70%, 50%)`} name={provinceName} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
            <TabsContent value="60d">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={transformedProvinceUsage60d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {uniqueProvinceNames60d.map((provinceName, index) => (
                    <Line key={provinceName} type="monotone" dataKey={provinceName} stroke={`hsl(${index * 40}, 70%, 50%)`} name={provinceName} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
            <TabsContent value="90d">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={transformedProvinceUsage90d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {uniqueProvinceNames90d.map((provinceName, index) => (
                    <Line key={provinceName} type="monotone" dataKey={provinceName} stroke={`hsl(${index * 40}, 70%, 50%)`} name={provinceName} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" />Token Usage by Location (Last 30 Days)</CardTitle>
          <CardDescription>Top locations by total token usage.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Country</TableHead>
                <TableHead>Province/State</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-right">Total Tokens</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.token_usage_by_location?.map((loc, index) => (
                <TableRow key={index}>
                  <TableCell>{loc.country || 'N/A'}</TableCell>
                  <TableCell>{loc.province || 'N/A'}</TableCell>
                  <TableCell>{loc.city || 'N/A'}</TableCell>
                  <TableCell className="text-right">{loc.total_tokens.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;